// Comprehensive products data organized by categories
// Based on the complete exportable products catalog
// Individual product images removed for cleaner, modern design

export const productCategories = [
  {
    id: 'horticultural',
    name: 'Horticultural Products',
    description: 'Fresh and processed horticultural products including fruits, vegetables, cut flowers, and medicinal herbs.',
    icon: 'Leaf',
    color: 'from-green-400 to-emerald-600',
    subcategories: [
      {
        id: 'fruits',
        name: 'Fruits',
        description: 'Premium fresh fruits from diverse agricultural regions across Africa.',
        image: 'https://images.pexels.com/photos/1099680/pexels-photo-1099680.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Avocados',
            description: 'Premium quality avocados with high oil content and excellent taste.',
            specifications: {
              'Grade': 'Export Grade A',
              'Size': '200-400g per fruit',
              'Oil Content': '18-25%',
              'Harvest': 'Year-round'
            }
          },
          {
            name: 'Bananas',
            description: 'Sweet, naturally ripened bananas perfect for export markets.',
            specifications: {
              'Variety': 'Cavendish, Lady Finger',
              'Size': '15-25cm length',
              'Quality': 'Premium export grade',
              'Harvest': 'Year-round'
            }
          },
          {
            name: 'Guava',
            description: 'Tropical guava fruits with exceptional vitamin C content.',
            specifications: {
              'Variety': 'Pink, White pulp',
              'Size': '50-200g per fruit',
              'Vitamin C': 'High content',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Lemons',
            description: 'Fresh citrus lemons with high acidity and essential oils.',
            specifications: {
              'Variety': 'Eureka, Lisbon',
              'Juice Content': '30-40%',
              'Size': '100-150g per fruit',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Mangoes',
            description: 'Tropical mangoes with exceptional sweetness and aroma.',
            specifications: {
              'Variety': 'Tommy Atkins, Kent, Keitt',
              'Brix Level': '12-16°',
              'Size': '300-600g per fruit',
              'Season': 'March-July'
            }
          },
          {
            name: 'Melon (Winter)',
            description: 'Sweet winter melons with excellent storage life.',
            specifications: {
              'Type': 'Honeydew, Cantaloupe',
              'Weight': '1-3kg per fruit',
              'Storage': '2-3 weeks',
              'Season': 'December-February'
            }
          },
          {
            name: 'Oranges',
            description: 'Citrus oranges with high vitamin C content and excellent juice quality.',
            specifications: {
              'Variety': 'Valencia, Navel',
              'Juice Content': '45-55%',
              'Size': '200-300g per fruit',
              'Season': 'November-May'
            }
          },
          {
            name: 'Pawpaw',
            description: 'Sweet papaya fruits with digestive enzymes and rich nutrients.',
            specifications: {
              'Weight': '0.5-3kg per fruit',
              'Sweetness': 'High brix',
              'Enzymes': 'Papain content',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Pineapples',
            description: 'Sweet, juicy pineapples with golden flesh and perfect acidity balance.',
            specifications: {
              'Variety': 'Smooth Cayenne, MD2',
              'Weight': '1.5-2.5kg per fruit',
              'Brix': '13-16°',
              'Crown': 'Fresh, healthy'
            }
          },
          {
            name: 'Plantains',
            description: 'Versatile plantains suitable for cooking and processing.',
            specifications: {
              'Variety': 'Green, Yellow, Ripe',
              'Length': '20-30cm',
              'Weight': '150-300g per fruit',
              'Harvest': 'Year-round'
            }
          },
          {
            name: 'Tomatoes',
            description: 'Fresh, vine-ripened tomatoes with excellent flavor and shelf life.',
            specifications: {
              'Variety': 'Roma, Cherry, Beefsteak',
              'Color': 'Red, firm',
              'Size': 'Medium to large',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Spinach',
            description: 'Fresh leafy spinach with high iron and vitamin content.',
            specifications: {
              'Type': 'Baby spinach, Mature',
              'Processing': 'Fresh, Frozen',
              'Nutrition': 'High iron, vitamins',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Red and White Sorrel (Hibiscus Sabdariffa)',
            description: 'Hibiscus flowers for tea production with antioxidant properties.',
            specifications: {
              'Type': 'Red, White varieties',
              'Processing': 'Dried flowers',
              'Use': 'Tea, beverages',
              'Season': 'November-January'
            }
          },
          {
            name: 'Dehydrated Vegetables',
            description: 'Processed dehydrated vegetables with extended shelf life.',
            specifications: {
              'Types': 'Mixed vegetables',
              'Moisture': '5-8%',
              'Storage': '12-24 months',
              'Packaging': 'Vacuum sealed'
            }
          }
        ]
      },
      {
        id: 'vegetables',
        name: 'Vegetables',
        description: 'Fresh vegetables cultivated using sustainable farming practices.',
        image: 'https://images.pexels.com/photos/1300972/pexels-photo-1300972.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Asparagus',
            description: 'Premium asparagus spears with tender texture.',
            specifications: {
              'Grade': 'Extra Class',
              'Length': '15-22cm',
              'Diameter': '12-16mm',
              'Season': 'March-June'
            }
          },
          {
            name: 'Beans',
            description: 'Fresh green beans with crisp texture and excellent flavor.',
            specifications: {
              'Variety': 'French beans, Runner beans',
              'Length': '10-15cm',
              'Quality': 'Grade I',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Baby Corn',
            description: 'Tender baby corn perfect for gourmet applications.',
            specifications: {
              'Length': '6-10cm',
              'Diameter': '1-2cm',
              'Processing': 'Fresh, Canned',
              'Grade': 'Premium'
            }
          },
          {
            name: 'Beetroot',
            description: 'Fresh beetroot with high sugar content and deep color.',
            specifications: {
              'Size': '5-8cm diameter',
              'Color': 'Deep red',
              'Sugar': 'High content',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Carrots',
            description: 'Sweet, crisp carrots with high beta-carotene content.',
            specifications: {
              'Length': '15-20cm',
              'Color': 'Orange, deep',
              'Beta-carotene': 'High',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Cucumbers',
            description: 'Fresh cucumbers with crisp texture and mild flavor.',
            specifications: {
              'Length': '20-25cm',
              'Diameter': '4-5cm',
              'Texture': 'Crisp, firm',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Garlic',
            description: 'Aromatic garlic bulbs with high allicin content.',
            specifications: {
              'Type': 'Purple, White varieties',
              'Clove Count': '8-12 per bulb',
              'Storage': '8-10 months',
              'Harvest': 'January-April'
            }
          },
          {
            name: 'Lettuce',
            description: 'Fresh lettuce varieties for salads and garnishing.',
            specifications: {
              'Variety': 'Iceberg, Romaine, Butter',
              'Weight': '200-400g per head',
              'Freshness': 'Premium grade',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Mushrooms',
            description: 'Cultivated mushrooms with excellent flavor and texture.',
            specifications: {
              'Variety': 'Button, Oyster, Shiitake',
              'Size': 'Medium to large',
              'Quality': 'Grade A',
              'Storage': 'Fresh, Dried'
            }
          },
          {
            name: 'Okra',
            description: 'Fresh okra pods with minimal fiber content, perfect for export.',
            specifications: {
              'Length': '8-15cm',
              'Color': 'Bright green',
              'Texture': 'Tender, crisp',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Onions',
            description: 'Premium quality onions with strong flavor and excellent storage life.',
            specifications: {
              'Variety': 'Red, White, Yellow',
              'Size': '5-10cm diameter',
              'Storage': '6-8 months',
              'Harvest': 'December-March'
            }
          },
          {
            name: 'Peas',
            description: 'Sweet garden peas with tender pods and excellent flavor.',
            specifications: {
              'Type': 'Garden peas, Snow peas',
              'Pod length': '8-12cm',
              'Sweetness': 'High sugar',
              'Season': 'Cool months'
            }
          },
          {
            name: 'Potatoes',
            description: 'High-quality potatoes suitable for various culinary applications.',
            specifications: {
              'Variety': 'Russet, Red, Fingerling',
              'Size': 'Medium to large',
              'Starch': 'High content',
              'Storage': '6-8 months'
            }
          }
        ]
      },
      {
        id: 'medicinal-herbs',
        name: 'Medicinal Herbs',
        description: 'Traditional medicinal herbs with therapeutic properties.',
        image: 'https://images.pexels.com/photos/1671822/pexels-photo-1671822.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Neem',
            description: 'Neem leaves and extracts with natural pesticide and medicinal properties.',
            specifications: {
              'Forms': 'Leaves, Oil, Extract',
              'Active Compounds': 'Azadirachtin',
              'Purity': '95%+',
              'Applications': 'Cosmetic, Medicinal'
            }
          },
          {
            name: 'Garlic (Medicinal)',
            description: 'Medicinal grade garlic for health supplements.',
            specifications: {
              'Allicin': 'High content',
              'Processing': 'Dried, Extract',
              'Grade': 'Pharmaceutical',
              'Certification': 'Organic'
            }
          },
          {
            name: 'Lemongrass',
            description: 'Aromatic lemongrass with essential oils for tea and aromatherapy.',
            specifications: {
              'Essential Oil': '0.2-0.5%',
              'Processing': 'Dried, Fresh, Oil',
              'Grade': 'Premium quality',
              'Season': 'Year-round'
            }
          },
          {
            name: 'Aloe Vera',
            description: 'Fresh aloe vera with high gel content for cosmetic and medicinal use.',
            specifications: {
              'Gel Content': '95%+',
              'Forms': 'Fresh leaves, Gel, Powder',
              'Purity': 'Organic certified',
              'Applications': 'Cosmetic, Health'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'agricultural-commodities',
    name: 'Agricultural Commodities',
    description: 'Primary agricultural commodities including cocoa, tubers, nuts, fibers, and spices.',
    icon: 'Wheat',
    color: 'from-amber-400 to-orange-600',
    subcategories: [
      {
        id: 'cocoa-products',
        name: 'Cocoa Products',
        description: 'Premium quality cocoa beans and products.',
        image: 'https://images.pexels.com/photos/918327/pexels-photo-918327.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Cocoa Beans (Light Crop)',
            description: 'Premium grade cocoa beans from the light crop harvest.',
            specifications: {
              'Grade': 'Grade I, Grade II',
              'Bean Size': '90-100 beans/100g',
              'Moisture': '7.5% max',
              'Season': 'May-September'
            }
          },
          {
            name: 'Cocoa Beans (Main Crop)',
            description: 'Premium grade cocoa beans from the main harvest season.',
            specifications: {
              'Grade': 'Grade I, Grade II',
              'Bean Size': '100-120 beans/100g',
              'Moisture': '7.5% max',
              'Season': 'October-January'
            }
          }
        ]
      },
      {
        id: 'tubers-derivatives',
        name: 'Tubers & Derivatives',
        description: 'Root vegetables and their processed products.',
        image: 'https://images.pexels.com/photos/144248/potatoes-vegetables-erdfrucht-bio-144248.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Cassava',
            description: 'Fresh cassava roots with high starch content.',
            specifications: {
              'Starch Content': '25-30%',
              'Size': 'Medium to large',
              'Quality': 'Premium grade',
              'Processing': 'Fresh, Dried'
            }
          },
          {
            name: 'Cassava Chips',
            description: 'Dried cassava chips for animal feed and industrial use.',
            specifications: {
              'Moisture': '12% max',
              'Starch': '65-70%',
              'Size': '2-5cm pieces',
              'Quality': 'Grade A'
            }
          },
          {
            name: 'Cassava Pulp',
            description: 'Cassava pulp for animal feed applications.',
            specifications: {
              'Moisture': '12% max',
              'Protein': '2-3%',
              'Fiber': '8-12%',
              'Use': 'Animal feed'
            }
          },
          {
            name: 'Cassava Flour',
            description: 'Fine cassava flour for food applications.',
            specifications: {
              'Fineness': '100 mesh',
              'Moisture': '10% max',
              'Purity': '99%+',
              'Color': 'White'
            }
          },
          {
            name: 'Cassava Starch',
            description: 'Pure cassava starch for industrial applications.',
            specifications: {
              'Purity': '98% min',
              'Moisture': '13% max',
              'pH': '5.0-7.0',
              'Whiteness': '90% min'
            }
          },
          {
            name: 'Cassava Glucose',
            description: 'Glucose syrup derived from cassava starch.',
            specifications: {
              'DE Value': '38-42',
              'Dry Matter': '80% min',
              'pH': '4.0-6.0',
              'Color': 'Clear'
            }
          },
          {
            name: 'Tapioca',
            description: 'Tapioca pearls and starch products.',
            specifications: {
              'Size': '2-4mm pearls',
              'Moisture': '14% max',
              'Starch': '85% min',
              'Color': 'White, translucent'
            }
          },
          {
            name: 'Gari',
            description: 'Traditional fermented cassava granules, a West African staple.',
            specifications: {
              'Moisture': '12% max',
              'Particle Size': '0.25-2mm',
              'Color': 'White, Yellow',
              'Fermentation': '3-5 days'
            }
          },
          {
            name: 'Starch',
            description: 'General purpose starch from various root crops.',
            specifications: {
              'Source': 'Cassava, Potato, Corn',
              'Purity': '95% min',
              'Moisture': '13% max',
              'Applications': 'Food, Industrial'
            }
          },
          {
            name: 'Yams',
            description: 'Fresh yams with excellent nutritional value.',
            specifications: {
              'Variety': 'White, Yellow yam',
              'Size': 'Large tubers',
              'Storage': '4-6 months',
              'Season': 'August-December'
            }
          },
          {
            name: 'Cocoyams',
            description: 'Nutritious cocoyam tubers for food applications.',
            specifications: {
              'Type': 'Taro, Tannia',
              'Size': 'Medium to large',
              'Nutrition': 'High carbohydrate',
              'Season': 'September-January'
            }
          },
          {
            name: 'Potatoes',
            description: 'Quality potatoes for fresh market and processing.',
            specifications: {
              'Variety': 'Processing, Table',
              'Size': 'Grade A',
              'Starch': 'High content',
              'Storage': '6-8 months'
            }
          }
        ]
      },
      {
        id: 'edible-nuts',
        name: 'Edible Nuts',
        description: 'Premium nuts for snacking and processing.',
        image: 'https://images.pexels.com/photos/1295572/pexels-photo-1295572.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Cashew Nuts (Raw)',
            description: 'Premium raw cashew nuts with excellent kernel quality.',
            specifications: {
              'Grade': 'W180, W210, W240, W320',
              'Moisture': '5% max',
              'Kernel Yield': '48-50%',
              'Season': 'February-May'
            }
          },
          {
            name: 'Ground Nuts',
            description: 'High-quality groundnuts (peanuts) for oil and food processing.',
            specifications: {
              'Variety': 'Spanish, Virginia',
              'Oil Content': '48-52%',
              'Moisture': '8% max',
              'Aflatoxin': '<4ppb'
            }
          },
          {
            name: 'Walnuts (in Shell)',
            description: 'Premium walnuts in shell with excellent kernel quality.',
            specifications: {
              'Size': 'Large, Medium',
              'Kernel Ratio': '45-50%',
              'Moisture': '8% max',
              'Quality': 'Grade A'
            }
          }
        ]
      },
      {
        id: 'natural-fibers',
        name: 'Natural Fibers',
        description: 'Sustainable natural fibers for textile and industrial use.',
        image: 'https://images.pexels.com/photos/6069112/pexels-photo-6069112.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Cotton Lint',
            description: 'High-quality cotton fiber for textile production.',
            specifications: {
              'Staple Length': '28-32mm',
              'Micronaire': '3.5-4.9',
              'Strength': '29+ g/tex',
              'Grade': 'Middling, SLM'
            }
          },
          {
            name: 'Jute',
            description: 'Natural jute fiber for sacking and textile applications.',
            specifications: {
              'Fiber Length': '2-4 meters',
              'Strength': 'High tensile',
              'Color': 'Golden to brown',
              'Grade': 'TD-1, TD-2'
            }
          },
          {
            name: 'Coconut Fibers',
            description: 'Coir fiber from coconut husks for various applications.',
            specifications: {
              'Length': '10-30cm',
              'Strength': 'High tensile',
              'Applications': 'Mats, Ropes, Brushes',
              'Grade': 'Brown, White'
            }
          }
        ]
      },
      {
        id: 'spices',
        name: 'Spices',
        description: 'Aromatic spices and seasonings for culinary use.',
        image: 'https://images.pexels.com/photos/531880/pexels-photo-531880.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Chilies',
            description: 'Hot chili peppers with high capsaicin content.',
            specifications: {
              'Variety': 'Scotch Bonnet, Cayenne',
              'Heat Level': '100,000-350,000 SHU',
              'Moisture': '10% max',
              'Processing': 'Dried, Powder'
            }
          },
          {
            name: 'Ginger (Dried, Split)',
            description: 'Premium dried ginger with high essential oil content.',
            specifications: {
              'Essential Oil': '1.5-3%',
              'Moisture': '10% max',
              'Processing': 'Split dried',
              'Grade': 'Export quality'
            }
          },
          {
            name: 'Pepper',
            description: 'Black and white pepper with strong aroma.',
            specifications: {
              'Type': 'Black, White pepper',
              'Piperine': '5-9%',
              'Moisture': '12% max',
              'Grade': 'TGSEB, GL'
            }
          },
          {
            name: 'Cloves',
            description: 'Aromatic cloves with high eugenol content.',
            specifications: {
              'Essential Oil': '15-20%',
              'Eugenol': '80-85%',
              'Moisture': '12% max',
              'Grade': 'Hand picked'
            }
          }
        ]
      },
      {
        id: 'gum-arabic',
        name: 'Gum Arabic',
        description: 'Natural gum arabic for food, pharmaceutical, and industrial applications.',
        image: 'https://images.pexels.com/photos/708777/pexels-photo-708777.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Gum Arabic Grade 1',
            description: 'Premium grade gum arabic with excellent clarity.',
            specifications: {
              'Purity': '99% min',
              'Ash': '4% max',
              'Moisture': '15% max',
              'Particle Size': '20-80 mesh'
            }
          },
          {
            name: 'Gum Arabic Grade 2',
            description: 'Standard grade gum arabic for general applications.',
            specifications: {
              'Purity': '95% min',
              'Ash': '5% max',
              'Moisture': '15% max',
              'Applications': 'Food, Industrial'
            }
          },
          {
            name: 'Gum Arabic Grade 3',
            description: 'Commercial grade gum arabic for industrial use.',
            specifications: {
              'Purity': '90% min',
              'Ash': '6% max',
              'Moisture': '15% max',
              'Use': 'Industrial applications'
            }
          }
        ]
      },
      {
        id: 'crude-drugs',
        name: 'Crude Drugs',
        description: 'Traditional medicinal plants and extracts.',
        image: 'https://images.pexels.com/photos/7262680/pexels-photo-7262680.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Kola Nuts (Fresh & Dried)',
            description: 'Traditional kola nuts with natural caffeine content.',
            specifications: {
              'Caffeine': '2-3%',
              'Moisture': '10% max (dried)',
              'Size': 'Large, Medium',
              'Grade': 'Premium quality'
            }
          },
          {
            name: 'Bitter Kola',
            description: 'Bitter kola seeds with medicinal properties.',
            specifications: {
              'Active Compounds': 'Garcinia',
              'Size': 'Whole seeds',
              'Processing': 'Dried',
              'Grade': 'Export quality'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'oil-seeds-derivatives',
    name: 'Oil Seeds & Derivatives',
    description: 'Oil-bearing seeds and their processed products including crude and refined oils.',
    icon: 'Droplet',
    color: 'from-yellow-400 to-amber-600',
    subcategories: [
      {
        id: 'raw-oil-seeds',
        name: 'Raw Oil Seeds',
        description: 'Premium oil-bearing seeds and nuts.',
        image: 'https://images.pexels.com/photos/4022089/pexels-photo-4022089.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Coconut (Fresh)',
            description: 'Mature coconuts with high copra content.',
            specifications: {
              'Copra Content': '65-70%',
              'Oil Content': '65-68%',
              'Weight': '400-800g per nut',
              'Maturity': '11-12 months'
            }
          },
          {
            name: 'Shea Nuts',
            description: 'Premium shea nuts with high fat content for butter production.',
            specifications: {
              'Fat Content': '45-55%',
              'Moisture': '7% max',
              'Quality': 'Grade A',
              'Season': 'June-September'
            }
          },
          {
            name: 'Coffee (Arabica/Robusta)',
            description: 'Premium coffee beans for roasting and processing.',
            specifications: {
              'Type': 'Arabica, Robusta',
              'Moisture': '12% max',
              'Screen Size': '15+ (Arabica)',
              'Grade': 'Grade 1, Grade 2'
            }
          },
          {
            name: 'Sesame Seeds (Beni Seeds)',
            description: 'High-quality sesame seeds with excellent oil content.',
            specifications: {
              'Oil Content': '48-52%',
              'Purity': '99% min',
              'Moisture': '6% max',
              'Color': 'White, Brown, Black'
            }
          },
          {
            name: 'Egusi (Melon)',
            description: 'Melon seeds for oil extraction and food processing.',
            specifications: {
              'Oil Content': '45-50%',
              'Protein': '25-30%',
              'Moisture': '8% max',
              'Size': 'Large kernels'
            }
          },
          {
            name: 'Sunflower Seeds',
            description: 'Premium sunflower seeds with high oil content.',
            specifications: {
              'Oil Content': '38-45%',
              'Moisture': '9% max',
              'Purity': '99% min',
              'Size': 'Large, uniform'
            }
          },
          {
            name: 'Pumpkin Seeds',
            description: 'Nutritious pumpkin seeds for oil and snacking.',
            specifications: {
              'Oil Content': '35-40%',
              'Protein': '25-30%',
              'Moisture': '8% max',
              'Grade': 'Premium'
            }
          }
        ]
      },
      {
        id: 'processed-oils',
        name: 'Processed/Manufactured Vegetable Oils & Cakes',
        description: 'Refined oils and processing by-products.',
        image: 'https://images.pexels.com/photos/33783/olive-oil-salad-dressing-cooking-olive.jpg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Cocoa Butter',
            description: 'Premium cocoa butter for chocolate and cosmetic industries.',
            specifications: {
              'Melting Point': '32-35°C',
              'Free Fatty Acid': '1.75% max',
              'Moisture': '0.2% max',
              'Color': 'Pale yellow'
            }
          },
          {
            name: 'Cocoa Cake',
            description: 'Cocoa cake remaining after oil extraction.',
            specifications: {
              'Fat Content': '10-12%',
              'Protein': '18-20%',
              'Moisture': '5% max',
              'Use': 'Animal feed, cocoa powder'
            }
          },
          {
            name: 'Coconut Oil (Crude/Refined)',
            description: 'Virgin and refined coconut oil for food and cosmetics.',
            specifications: {
              'Type': 'Virgin, Refined',
              'Lauric Acid': '45-50%',
              'Moisture': '0.1% max',
              'Free Fatty Acid': '0.5% max (refined)'
            }
          },
          {
            name: 'Fish Oil (Crude and Refined)',
            description: 'Fish oil for nutritional and industrial applications.',
            specifications: {
              'Omega-3': '25-30%',
              'EPA/DHA': 'High content',
              'Peroxide Value': 'Low',
              'Grade': 'Food, Feed'
            }
          },
          {
            name: 'Shea Butter Products',
            description: 'Unrefined and refined shea butter for cosmetics.',
            specifications: {
              'Type': 'Raw, Refined',
              'Melting Point': '32-38°C',
              'Moisture': '0.05% max',
              'Color': 'Ivory to light yellow'
            }
          },
          {
            name: 'Palm Kernel Oil (Crude/Refined)',
            description: 'Palm kernel oil for food and industrial use.',
            specifications: {
              'Type': 'Crude, Refined',
              'Lauric Acid': '48-52%',
              'Free Fatty Acid': '5% max (crude)',
              'Moisture': '0.1% max'
            }
          },
          {
            name: 'Palm Kernel Cake',
            description: 'Palm kernel cake for animal feed.',
            specifications: {
              'Protein': '14-18%',
              'Fat': '8-10%',
              'Fiber': '15-18%',
              'Moisture': '10% max'
            }
          },
          {
            name: 'Sesame Seed Oil',
            description: 'Premium sesame oil with distinctive flavor.',
            specifications: {
              'Type': 'Cold pressed, Refined',
              'Oleic Acid': '35-40%',
              'Linoleic Acid': '40-45%',
              'Smoking Point': '210°C'
            }
          },
          {
            name: 'Ginger Oil',
            description: 'Essential ginger oil for food and aromatherapy.',
            specifications: {
              'Purity': '100% pure',
              'Extraction': 'Steam distilled',
              'Gingerol': 'High content',
              'Color': 'Pale yellow'
            }
          },
          {
            name: 'Soya Bean Oil (Crude/Refined)',
            description: 'Soybean oil for cooking and industrial applications.',
            specifications: {
              'Type': 'Crude, Refined',
              'Linoleic Acid': '50-55%',
              'Free Fatty Acid': '0.05% max (refined)',
              'Iodine Value': '120-140'
            }
          },
          {
            name: 'Soya Bean Cake',
            description: 'High-protein soybean meal for animal feed.',
            specifications: {
              'Protein': '44-48%',
              'Fat': '1.5% max',
              'Moisture': '12% max',
              'Fiber': '7% max'
            }
          },
          {
            name: 'Copra Oil',
            description: 'Oil extracted from dried coconut meat.',
            specifications: {
              'Lauric Acid': '45-50%',
              'Free Fatty Acid': '5% max',
              'Moisture': '0.2% max',
              'Type': 'Crude, Semi-refined'
            }
          },
          {
            name: 'Castor Oil',
            description: 'Industrial grade castor oil for various applications.',
            specifications: {
              'Ricinoleic Acid': '85-90%',
              'Viscosity': '650-750 cP',
              'Acid Value': '2 max',
              'Grade': 'Industrial, Pharmaceutical'
            }
          },
          {
            name: 'Maize Oil',
            description: 'Corn oil for cooking and food processing.',
            specifications: {
              'Linoleic Acid': '55-60%',
              'Oleic Acid': '25-30%',
              'Free Fatty Acid': '0.05% max',
              'Smoke Point': '232°C'
            }
          },
          {
            name: 'Groundnut Oil (Crude and Refined)',
            description: 'Peanut oil for cooking and industrial use.',
            specifications: {
              'Type': 'Crude, Refined',
              'Oleic Acid': '40-60%',
              'Linoleic Acid': '20-35%',
              'Smoke Point': '225°C'
            }
          },
          {
            name: 'Sunflower Seed Oil (Crude and Refined)',
            description: 'Light sunflower oil for cooking applications.',
            specifications: {
              'Type': 'Crude, Refined',
              'Linoleic Acid': '60-70%',
              'Oleic Acid': '15-25%',
              'Vitamin E': 'High content'
            }
          },
          {
            name: 'Cotton Seed Oil',
            description: 'Cotton seed oil for food and industrial applications.',
            specifications: {
              'Linoleic Acid': '50-55%',
              'Palmitic Acid': '20-25%',
              'Free Fatty Acid': '0.05% max',
              'Gossypol': 'Removed (refined)'
            }
          },
          {
            name: 'Cotton Seed Cake',
            description: 'Cotton seed meal for animal feed and fertilizer.',
            specifications: {
              'Protein': '20-24%',
              'Fat': '1.5% max',
              'Fiber': '12-15%',
              'Gossypol': '<0.04%'
            }
          }
        ]
      }
    ]
  },
  {
    id: 'processed-products',
    name: 'Processed/Manufactured Products',
    description: 'Value-added processed agricultural products for various industries.',
    icon: 'Package',
    color: 'from-purple-400 to-indigo-600',
    subcategories: [
      {
        id: 'general-processed',
        name: 'General Processed Products',
        description: 'Various processed agricultural products for industrial and consumer use.',
        image: 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg?auto=compress&cs=tinysrgb&w=800',
        products: [
          {
            name: 'Processed Food Products',
            description: 'Various processed food items from agricultural raw materials.',
            specifications: {
              'Types': 'Flours, Snacks, Beverages',
              'Quality': 'Food grade',
              'Packaging': 'Consumer ready',
              'Shelf Life': 'Extended'
            }
          },
          {
            name: 'Industrial Raw Materials',
            description: 'Processed agricultural materials for industrial applications.',
            specifications: {
              'Applications': 'Textiles, Chemicals, Pharma',
              'Quality': 'Industrial grade',
              'Processing': 'Value-added',
              'Standards': 'International'
            }
          }
        ]
      }
    ]
  }
];

// Generate flat list of all products for search functionality
export const allProducts = [];

productCategories.forEach(category => {
  category.subcategories.forEach(subcategory => {
    subcategory.products.forEach(product => {
      allProducts.push({
        ...product,
        categoryName: category.name,
        subcategoryName: subcategory.name,
        categoryId: category.id,
        subcategoryId: subcategory.id
      });
    });
  });
});

export default { productCategories, allProducts }; 